import * as query from "@arcgis/core/rest/query";
import Query from "@arcgis/core/rest/support/Query";
import FieldNames from "../defaults/FieldNames.json";
import GeneralService from "./GeneralService";
import Request from "./request";
import {
  getAppConfig,
  getAttributeServiceUrl,
  getNifcToken,
} from "../utils/ReduxHelper";

const CarryoverService = {
  getCarryoverReasons: function () {
    try {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const queryObject = new Query({
        where: "(OBJECTID IS NOT NULL)",
        outFields: "*",
        returnGeometry: false,
      });

      return query
        .executeQueryJSON(
          rootServiceUrl + "/" + config.carryoverReasons[config.env].layerId,
          queryObject
        )
        .then((results) => {
          let reasons = [];
          if (results.features) {
            reasons = results.features.map(
              (eaItem) => eaItem.attributes.Reason
            );
          }
          return reasons;
        });
    } catch (e) {
      return new Promise((resolve, reject) => {
        reject(e);
      });
    }
  },
  addCarryoverEntry: function (carryoverObj) {
    try {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const nifcToken = getNifcToken();
      const newCarryoverEntry = {
        attributes: carryoverObj,
      };
      const layerID = config.carryover[config.env].layerId;

      return new Promise((resolve, reject) => {
        new Request()
          .makeRequest({
            url: rootServiceUrl + "/" + layerID + "/addFeatures",
            method: "post",
            data: {
              f: "json",
              token: nifcToken,
              features: JSON.stringify([newCarryoverEntry]),
            },
          })
          .then(
            (resp) => {
              resolve(resp);
            },
            (error) => {
              reject(error);
            }
          );
      });
    } catch (e) {
      return new Promise((resolve, reject) => {
        reject(e);
      });
    }
  },
  updateCarryoverEntry: function (carryoverUpdateObj) {
    try
    {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const nifcToken = getNifcToken();
      let carryoverObj = {
        attributes: carryoverUpdateObj
      };

      return new Promise((resolve, reject) => {
        new Request().makeRequest({
          url: rootServiceUrl + "/" + config.carryover[config.env].layerId + "/updateFeatures",
          method: 'post',
          data: {
              f: 'json',
              token: nifcToken,
              features: JSON.stringify([carryoverObj])
          }
      }).then(
          resp => {
            resolve(resp);
          },
          error => {
            reject(error)
          }
      )
      });
    }
    catch(e)
    {
      return new Promise((resolve,reject) => {
        reject(e);
      });
    }
  },
  addMultipleCarryoverEntries: async function (carryoverItems) {
    try {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      const nifcToken = getNifcToken();
      const layerID = config.carryover[config.env].layerId;

      let allFinalResults = [];
      let carryoverList = [];
      carryoverItems.forEach((carryoverObj) => {
        const newCarryoverEntry = {
          attributes: carryoverObj,
        };

        carryoverList.push(newCarryoverEntry);
      });

      let featureUrl = `${rootServiceUrl}/${layerID}/addFeatures`;

      let newLogs = [];
      carryoverList.forEach((log) => {
        const newFeature = {
          attributes: log.attributes,
        };
        newLogs.push(newFeature);
      });
      const numberToTakeEachTime = 500;
      let startCount = 0;
      let hasMoreRecords = true;
      let finalResults = "";
      while (hasMoreRecords) {
        const numberToTake =
          startCount + numberToTakeEachTime > newLogs.length
            ? newLogs.length - startCount
            : numberToTakeEachTime;
        let numbersToRun = newLogs.slice(startCount, numberToTake + startCount);
        try {
          finalResults = await GeneralService.updateCallForMultiples(
            featureUrl,
            nifcToken,
            numbersToRun
          );
          if (finalResults && finalResults.addResults) {
            allFinalResults.push(finalResults.addResults);
          }
        } catch (e) {
          numbersToRun.forEach((item) => {
            const newItem = {
              objectId: item.attributes[FieldNames.featureFields.objectID],
              success: false,
            };
            allFinalResults.push(newItem);
          });
        }
        hasMoreRecords = newLogs.length > startCount + numberToTakeEachTime;
        startCount += numberToTakeEachTime;
      }
      let failedItems = [];
      let successfulItems = [];
      if (!hasMoreRecords) {
        allFinalResults.flat().forEach((result) => {
          if (result) {
            if (!result.success) {
              failedItems.push(result.objectId);
            } else {
              successfulItems.push(result.objectId);
            }
          }
        });
      }

      const allItemResults = {
        successful: successfulItems,
        failures: failedItems,
      };
      return allItemResults;
    } catch (e) {
      return new Promise((resolve, reject) => {
        reject(e);
      });
    }
  },
  getLatestMultipleFeaturesCarryoverEntry: async function (
    entityGeoType,
    entityIdList,
    outFields = "*"
  ) {
    try {
      const config = getAppConfig();
      const rootServiceUrl = getAttributeServiceUrl();
      if (!entityIdList || entityIdList.length < 1) {
        return new Promise((resolve, reject) => {
          resolve([]);
        });
      }
      const layerID = config.carryover[config.env].layerId;

      let allFinalResults = [];
      const numberToTakeEachTime = 2000;
      let startIndex = 0;
      let hasMoreRecords = true;
      let finalResults = "";
      while (hasMoreRecords) {
        try {
          let endIndex = startIndex + numberToTakeEachTime;
          let entityIdBatch = [];
          entityIdBatch = entityIdList.slice(startIndex, endIndex);
          let entityIdBatchStr = entityIdBatch.join(",");
          let batchWhere =
            `((JoinFeature='${entityGeoType}') AND (JoinID IN (` +
            entityIdBatchStr +
            ")))";

          const queryObject = new Query({
            where: batchWhere,
            orderByFields: "OBJECTID DESC",
            outFields: outFields,
            returnGeometry: false,
            num: numberToTakeEachTime,
          });
          finalResults = await GeneralService.queryCall(
            rootServiceUrl + "/" + layerID,
            queryObject
          );
          if (finalResults && finalResults.length > 0) {
            allFinalResults.push(finalResults);
          }
        } catch (e) {
          throw e;
        }
        hasMoreRecords =
          entityIdList.length > startIndex + numberToTakeEachTime;
        startIndex += numberToTakeEachTime;
      }
      const allFinalResultsFlattened =
        allFinalResults != null && allFinalResults.length > 0
          ? allFinalResults.flat()
          : [];
      const allCarryoverEntries = allFinalResultsFlattened.map((r) => {
        let retObj = {
          [FieldNames.carryoverFields.joinID]:
            r[FieldNames.carryoverFields.joinID],
          [FieldNames.carryoverFields.joinFeature]:
            r[FieldNames.carryoverFields.joinFeature],
          Reason: r[FieldNames.carryoverFields.reason],
          TargetFiscalYear: r[FieldNames.carryoverFields.targetFiscalYear],
        };
        return retObj;
      });
      return allCarryoverEntries;
    } catch (e) {
      return new Promise((resolve, reject) => {
        reject(e);
      });
    }
  },
  getAllOrderedCarryoverForFeature: async function (joinID, joinFeature) {
      try
      {
        const config = getAppConfig();
        const rootServiceUrl = getAttributeServiceUrl();
        let baseWhereClause = `((JoinID = ${joinID}) AND (JoinFeature='${joinFeature}'))`;
        const url = `${rootServiceUrl}/${config.carryover[config.env].layerId}`;
  
        let allFinalResults = [];
        const numberToTakeEachTime = 2000;
        let startIndex = 0;
        let hasMoreRecords = true;
        while(hasMoreRecords){
          const queryObject = new Query({
            where: baseWhereClause,
            outFields: "*",
            orderByFields: "CreatedOnDate",
            start: startIndex,
            num: numberToTakeEachTime,
            returnDistinctValues: true
         });
          let interimResults = await GeneralService.queryCall(url, queryObject);
          if(interimResults && interimResults.length > 0){
            allFinalResults.push(interimResults);
            if(interimResults.length === numberToTakeEachTime){
              startIndex += numberToTakeEachTime;
              queryObject.start = startIndex;
            } else {
              hasMoreRecords = false;
            }
          }
          else {
            hasMoreRecords = false;
          }
        }
  
        const allFinalResultsFlattened = (allFinalResults != null && allFinalResults.length > 0) ? allFinalResults.flat() : [];
        return allFinalResultsFlattened;
      }
      catch(e)
      {
        return new Promise((resolve,reject) => {
          reject(e);
        });
      }
    },
};

export default CarryoverService;
